import type { SoulMapDocumentStatus } from '@/services/practice-service';
import type { MyVariable } from '@/types/variables';

/**
 * Per-tenant user variable that switches the "Your Soul Map is ready" modal
 * on. Its value is the newest `generated_at` the user has acknowledged.
 */
export const SOUL_MAP_READY_SEEN_VARIABLE = 'soul_map_ready_seen';

export const SOUL_MAP_COMPLETION_KIND = 'soul_map';

export interface SoulMapPracticeSummary {
  id: number;
  name?: string | null;
  completion_kind?: string | null;
  completed_at?: string | null;
}

export interface SoulMapReadyItem {
  practiceId: number;
  practiceName: string;
  generatedAt: string;
  downloadUrl: string | null;
}

export type SoulMapReadyCheckResult =
  | { enabled: false }
  | { enabled: true; watermark: string | null; items: SoulMapReadyItem[] };

export interface SoulMapReadyCheckDeps {
  getMyVariables: () => Promise<MyVariable[]>;
  getPractices: () => Promise<SoulMapPracticeSummary[]>;
  getSoulMapDocuments: (
    practiceId: number
  ) => Promise<SoulMapDocumentStatus | null>;
}

export function parseSoulMapDate(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

/** `undefined` when the variable does not exist, so the feature is off. */
export function findSoulMapReadySeenVariable(
  variables: MyVariable[]
): MyVariable | undefined {
  return variables.find(
    (variable) => variable.name === SOUL_MAP_READY_SEEN_VARIABLE
  );
}

export function isSoulMapReadySeenVariable(variable: { name?: string }) {
  return variable.name === SOUL_MAP_READY_SEEN_VARIABLE;
}

/**
 * A ready PDF the user has neither acknowledged (generated after the
 * watermark) nor downloaded since it was generated. The download check covers
 * users who opened the PDF from the email link or before this feature shipped.
 */
export function isUnseenSoulMapPdf(
  document: SoulMapDocumentStatus | null | undefined,
  watermark: string | null
): boolean {
  if (document?.status !== 'ready') return false;

  const generatedAt = parseSoulMapDate(document.generated_at);
  if (generatedAt === null) return false;

  const acknowledgedAt = parseSoulMapDate(watermark);
  if (acknowledgedAt !== null && generatedAt <= acknowledgedAt) return false;

  const lastDownloadedAt = parseSoulMapDate(
    document.documents?.full?.pdf?.last_downloaded_at
  );
  return lastDownloadedAt === null || lastDownloadedAt < generatedAt;
}

/** The newest `generated_at` among the items, as the original string. */
export function getNewestGeneratedAt(items: SoulMapReadyItem[]): string | null {
  let newest: SoulMapReadyItem | null = null;
  for (const item of items) {
    if (
      !newest ||
      (parseSoulMapDate(item.generatedAt) ?? 0) >
        (parseSoulMapDate(newest.generatedAt) ?? 0)
    ) {
      newest = item;
    }
  }
  return newest?.generatedAt ?? null;
}

export async function checkSoulMapReady(
  deps: SoulMapReadyCheckDeps
): Promise<SoulMapReadyCheckResult> {
  const variable = findSoulMapReadySeenVariable(await deps.getMyVariables());
  if (!variable) return { enabled: false };

  const watermark =
    typeof variable.value === 'string' && variable.value.trim()
      ? variable.value
      : null;

  const practices = (await deps.getPractices()).filter(
    (practice) => practice.completion_kind === SOUL_MAP_COMPLETION_KIND
  );

  const results = await Promise.allSettled(
    practices.map(async (practice) => ({
      practice,
      document: await deps.getSoulMapDocuments(practice.id),
    }))
  );

  const items: SoulMapReadyItem[] = [];
  for (const result of results) {
    if (result.status === 'rejected') {
      // One practice failing should not hide the others.
      console.error('Soul Map ready check failed for a practice:', result.reason);
      continue;
    }
    const { practice, document } = result.value;
    if (!document || !isUnseenSoulMapPdf(document, watermark)) continue;
    items.push({
      practiceId: practice.id,
      practiceName: practice.name?.trim() || 'Soul Map',
      generatedAt: document.generated_at as string,
      downloadUrl: document.downloads?.full ?? null,
    });
  }

  items.sort(
    (a, b) =>
      (parseSoulMapDate(b.generatedAt) ?? 0) -
      (parseSoulMapDate(a.generatedAt) ?? 0)
  );

  return { enabled: true, watermark, items };
}

/**
 * Session-only state, kept outside React so remounts and route changes do not
 * reset it. Keyed by tenant and user, since the variable is tenant-scoped.
 */
interface SoulMapReadySessionEntry {
  /** Set after a failed PATCH: no more modals this session. */
  hidden: boolean;
  /** Newest `generated_at` of each modal already shown this session. */
  shownWatermarks: Set<string>;
}

const sessions = new Map<string, SoulMapReadySessionEntry>();

function getSession(sessionKey: string): SoulMapReadySessionEntry {
  let session = sessions.get(sessionKey);
  if (!session) {
    session = { hidden: false, shownWatermarks: new Set() };
    sessions.set(sessionKey, session);
  }
  return session;
}

export function shouldShowSoulMapReadyModal(
  sessionKey: string,
  result: SoulMapReadyCheckResult | undefined
): result is Extract<SoulMapReadyCheckResult, { enabled: true }> {
  if (!result?.enabled || result.items.length === 0) return false;
  const session = getSession(sessionKey);
  if (session.hidden) return false;
  const newest = getNewestGeneratedAt(result.items);
  return newest !== null && !session.shownWatermarks.has(newest);
}

export function markSoulMapReadyModalShown(
  sessionKey: string,
  items: SoulMapReadyItem[]
) {
  const newest = getNewestGeneratedAt(items);
  if (newest) getSession(sessionKey).shownWatermarks.add(newest);
}

/**
 * Stores the newest shown `generated_at` as the watermark. `applyLocally`
 * runs first so the modal cannot come back while the request is in flight.
 * A failed PATCH hides the modal for the rest of the session; it is logged,
 * never retried, and never shown to the user.
 */
export async function acknowledgeSoulMapReady(
  sessionKey: string,
  items: SoulMapReadyItem[],
  deps: {
    patch: (watermark: string) => Promise<unknown>;
    applyLocally: (watermark: string) => void;
  }
): Promise<boolean> {
  const watermark = getNewestGeneratedAt(items);
  if (!watermark) return false;

  markSoulMapReadyModalShown(sessionKey, items);
  deps.applyLocally(watermark);

  try {
    await deps.patch(watermark);
    return true;
  } catch (error) {
    getSession(sessionKey).hidden = true;
    console.error('Failed to save soul_map_ready_seen:', error);
    return false;
  }
}

/** Test helper. */
export function resetSoulMapReadySessions() {
  sessions.clear();
}
